import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

/**
 * WS2 (D75) — registry-fed dropdown for Type / Channel with an inline
 * "+ Add new…" option. Producers, DMs, and admins can add a missing
 * option without leaving the submission form (Fredrik 2026-08-11: "we
 * need to be able to add options in the interface if the one we want
 * doesn't exist").
 *
 * New channels are created server-side with postApprovalAction='manual'
 * until an admin re-maps them (D75 safety rule) — the caller doesn't
 * need to care.
 */

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

export interface TaxonomyOption {
  _id: string;
  kind: 'type' | 'channel' | 'audience' | 'topic' | 'geography';
  label: string;
  // v2 (2026-08-14) — immutable identity + path segment. topic/geography
  // dropdowns STORE the slug (relabelling then costs nothing); the older
  // dimensions still store labels this phase.
  slug?: string;
  parent?: string | null;
  postApprovalAction: 'content_hub' | 'website_page' | 'portal_content' | 'manual' | null;
  accessType?: 'PUBLIC_INDEXABLE' | 'PUBLIC_NOT_INDEXABLE' | 'PRIVATE' | null;
  allowedFormats?: string[];
  folderNodeId?: string | null;
  active: boolean;
  seeded?: boolean;
  addedBy?: { teamMemberId?: string | null; name?: string | null };
}

const ADD_NEW = '__add_new__';

interface TaxonomySelectProps {
  kind: 'type' | 'channel' | 'audience' | 'topic' | 'geography';
  value: string;
  // Receives the option's VALUE — its slug for slug-valued kinds
  // (topic/geography), its label otherwise.
  onChange: (value: string) => void;
  getAuthHeaders: () => Promise<Record<string, string>>;
  // Rendered as the empty-value option, e.g. "Select..."
  placeholder?: string;
  disabled?: boolean;
  // Channels only — hide channels whose allowedFormats excludes this
  // detected Format (empty allowedFormats = accepts everything).
  filterFormat?: string | null;
}

// Slug-valued kinds: the stored identity is the slug, so renames are free.
const SLUG_VALUED_KINDS = ['topic', 'geography'];

export const TaxonomySelect: React.FC<TaxonomySelectProps> = ({
  kind, value, onChange, getAuthHeaders, placeholder = 'Select...', disabled = false, filterFormat = null
}) => {
  const valueOf = (o: TaxonomyOption) =>
    SLUG_VALUED_KINDS.includes(kind) ? (o.slug || o.label) : o.label;
  const [options, setOptions] = useState<TaxonomyOption[]>([]);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOptions = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get<TaxonomyOption[]>(
        `${backendUrl}/api/content-review/taxonomy?kind=${kind}`, { headers }
      );
      setOptions(res.data || []);
    } catch {
      setOptions([]);
    }
  }, [kind, getAuthHeaders]);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  async function handleAdd() {
    const label = newLabel.trim();
    if (label.length < 2) { setError('At least 2 characters'); return; }
    try {
      setBusy(true);
      setError(null);
      const headers = await getAuthHeaders();
      const res = await axios.post<TaxonomyOption>(
        `${backendUrl}/api/content-review/taxonomy`,
        { kind, label }, { headers }
      );
      await fetchOptions();
      onChange(SLUG_VALUED_KINDS.includes(kind) ? (res.data.slug || res.data.label) : res.data.label);
      setAdding(false);
      setNewLabel('');
    } catch (err: any) {
      const data = err.response?.data;
      // 409 with an existing option: just select it — that's what the
      // user wanted.
      if (err.response?.status === 409 && data?.existing?.label) {
        onChange(SLUG_VALUED_KINDS.includes(kind) ? (data.existing.slug || data.existing.label) : data.existing.label);
        setAdding(false);
        setNewLabel('');
      } else {
        setError(data?.error || 'Failed to add option');
      }
    } finally {
      setBusy(false);
    }
  }

  if (adding) {
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          autoFocus
          value={newLabel}
          onChange={e => setNewLabel(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
            if (e.key === 'Escape') { setAdding(false); setNewLabel(''); setError(null); }
          }}
          placeholder={`New ${kind}...`}
          disabled={busy}
          data-testid={`taxonomy-add-input-${kind}`}
          style={{ flex: 1 }}
        />
        <button type="button" className="review-btn review-btn-primary" onClick={handleAdd} disabled={busy}
          style={{ fontSize: 12, padding: '6px 10px' }}>
          {busy ? '...' : 'Add'}
        </button>
        <button type="button" className="review-btn review-btn-outline"
          onClick={() => { setAdding(false); setNewLabel(''); setError(null); }}
          style={{ fontSize: 12, padding: '6px 10px' }}>
          Cancel
        </button>
        {error && <span style={{ color: '#dc3545', fontSize: 11 }}>{error}</span>}
      </div>
    );
  }

  const filtered = options.filter(
    o => !filterFormat || !o.allowedFormats || o.allowedFormats.length === 0 || o.allowedFormats.includes(filterFormat)
  );
  // v2 — one-level hierarchy: parents first, each followed by its children
  // (indented). Roots keep the fetch's label order.
  const roots = filtered.filter(o => !o.parent);
  const childrenOf = (id: string) => filtered.filter(o => o.parent === id);
  const visibleOptions: Array<TaxonomyOption & { __depth?: number }> = [];
  for (const r of roots) {
    visibleOptions.push(r);
    for (const c of childrenOf(r._id)) visibleOptions.push({ ...c, __depth: 1 });
  }
  // Orphans whose parent is filtered out / inactive still render, flat.
  for (const o of filtered) {
    if (o.parent && !visibleOptions.some(v => v._id === o._id)) visibleOptions.push(o);
  }
  // Walk fix 2026-08-12 (Fredrik, station 3) — the current value must stay
  // visible even when format filtering excludes it (a grandfathered cycle,
  // e.g. a pptx already on Content Hub, rendered as a BLANK select). Key
  // the fallback on the FILTERED list, and label the format-mismatch case
  // so it's clear why the option isn't normally offered.
  const currentHidden = value && !visibleOptions.some(o => valueOf(o) === value);
  const currentIsKnown = options.some(o => valueOf(o) === value);

  return (
    <select
      value={value}
      onChange={e => {
        if (e.target.value === ADD_NEW) { setAdding(true); return; }
        onChange(e.target.value);
      }}
      disabled={disabled}
      data-testid={`taxonomy-select-${kind}`}
    >
      <option value="">{placeholder}</option>
      {visibleOptions.map(o => (
        <option key={o._id} value={valueOf(o)}>
          {o.__depth ? `\u00A0\u00A0\u2514 ${o.label}` : o.label}
        </option>
      ))}
      {/* Keep the current value selectable when it isn't among the offered
          options — legacy labels AND format-filtered grandfathered values. */}
      {currentHidden && (
        <option value={value}>
          {value}{currentIsKnown && filterFormat ? ' (current — format mismatch)' : ''}
        </option>
      )}
      <option value={ADD_NEW}>+ Add new…</option>
    </select>
  );
};

export default TaxonomySelect;
